<template>
	<a
		:href="link"
		:class="classes"
		target="_blank"
		rel="noopener noreferrer"
		:title="link"
		:aria-label="link"
	>
		<UIShIcon :name="icon" class="icon" />
	</a>
</template>

<script setup lang="ts">
const SIZES = ['md', 'lg', 'xl'] as const;
type SizesT = 'md' | 'lg' | 'xl';
type SizeName = `size-${(typeof SIZES)[number]}`;
type SizeNameAndValue = `size-${(typeof SIZES)[number]}${number}`;
type UISizeBckPoint = SizeName | SizeNameAndValue;

export type ShLinkSocialT = {
	link: string;
	icon: string;
	kind?: 'gray' | 'white';
} & { [s in SizesT]?: number | boolean };

const props = withDefaults(defineProps<ShLinkSocialT>(), {
	kind: 'gray'
});

const classes = computed(() => {
	const sizeClass = getSizeClass();

	return {
		'link-social': true,
		[`link-${props.kind}`]: true,
		...sizeClass
	};
});

function getSizeClass() {
	const classes: { [key in UISizeBckPoint]?: boolean } = {};

	SIZES.forEach((s) => {
		const value = props[s];

		if (!(s in props)) return;
		if (value === undefined) return;

		if (typeof value === 'boolean') {
			return (classes[`size-${s}`] = value);
		}

		classes[`size-${s}${value}`] = true;
	});

	return classes;
}
</script>

<style scoped lang="scss">
.link-social {
	display: flex;
	flex-wrap: nowrap;
	cursor: pointer;
	overflow: hidden;
	align-items: center;
	justify-content: center;
	border-radius: 1.778rem;
}

.link-gray {
	color: var(--color-brand500);
	background-color: var(--color-gray900);

	&:hover {
		color: var(--color-white);
		background-color: var(--color-brand600);
	}
}

.link-white {
	color: var(--color-brand500);
	background-color: var(--color-white);

	&:hover {
		color: var(--color-white);
		background-color: var(--color-brand600);
	}
}

$sizes: md, lg, xl;
$screens: 768, 1024, 1240, 1440, 1920;

@mixin useLink($size) {
	@include useFixedSize(
		var(--link-soc-#{$size}-size),
		var(--link-soc-#{$size}-size)
	);

	--icon-width: var(--link-soc-#{$size}-icon);
	--icon-height: var(--link-soc-#{$size}-icon);
}

@each $size in $sizes {
	.size-#{$size} {
		@include useLink($size);
	}
}

@each $screen in $screens {
	@each $size in $sizes {
		.size-#{$size}#{$screen} {
			@media screen and (min-width: #{$screen}px) {
				@include useLink($size);
			}
		}
	}
}
</style>
